export const DEFAULT_AUTHENTICATED_PATH = "/projects";

const returnUrlOrigin = "https://auth-return.invalid";
const prohibitedCharacters = /[\\\u0000-\u0020\u007f]/;
const prohibitedPathEscapes = /%(?:2f|5c|25|0[0-9a-f]|1[0-9a-f]|7f)/i;

export function normalizeReturnUrl(returnUrl?: unknown): string | null {
  if (
    typeof returnUrl !== "string" ||
    !returnUrl.startsWith("/") ||
    returnUrl.startsWith("//") ||
    prohibitedCharacters.test(returnUrl)
  ) {
    return null;
  }

  // Search-param values have already been decoded once by the routing adapter.
  // Only inspect pathname escapes; encoded query and fragment values stay opaque.
  const pathname = returnUrl.split(/[?#]/, 1)[0];
  if (prohibitedPathEscapes.test(pathname)) {
    return null;
  }

  try {
    decodeURIComponent(pathname);
    const url = new URL(returnUrl, returnUrlOrigin);
    if (url.origin !== returnUrlOrigin || url.pathname.startsWith("//")) {
      return null;
    }

    const decodedPathname = decodeURIComponent(url.pathname);
    if (
      [url.pathname, decodedPathname].some(
        (path) => path.startsWith("/login") || path.startsWith("/logout"),
      )
    ) {
      return null;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function getLoginRedirectPath(pathname: string, search = ""): string {
  const returnUrl = normalizeReturnUrl(`${pathname}${search}`);

  if (!returnUrl || returnUrl === "/") {
    return "/login";
  }

  const params = new URLSearchParams({ returnUrl });
  return `/login?${params.toString()}`;
}

export function getAuthPath(pathname: string, returnUrl?: unknown): string {
  const normalizedReturnUrl = normalizeReturnUrl(returnUrl);

  if (!normalizedReturnUrl || normalizedReturnUrl === "/") {
    return pathname;
  }

  const params = new URLSearchParams({ returnUrl: normalizedReturnUrl });
  return `${pathname}?${params.toString()}`;
}

export function getAuthenticatedLandingPath(returnUrl?: unknown): string {
  return normalizeReturnUrl(returnUrl) ?? DEFAULT_AUTHENTICATED_PATH;
}
